import { FC, ReactElement, memo } from "react";

import { Theme } from "apps/website/maps/Theme.map";
import Column from "apps/website/components/layout/Column/Column";
import {
  gridGapsMap,
  gridMaxColumnsMap,
} from "apps/website/components/layout/Grid/Grid.map";
import * as GridMap from "apps/website/components/layout/Grid/Grid.map";
import { WithTestID } from "apps/website/types";
import cx from "apps/website/utils/misc/cx";

export interface IGrid extends WithTestID {
  /** Unique identifier for the grid element */
  id?: string;
  /** HTML tag to render (div, ul, ol, article) */
  tag?: GridMap.AvailableTag;
  /** Gap size between grid columns */
  gap?: GridMap.Gap,
  /** Maximum number of columns for responsive behavior */
  maxColumns?: GridMap.MaxColumns,
  /** Additional CSS classes applied to the grid */
  className?: string;
  /** Theme applied to the grid for styling */
  theme?: Theme;
  /** Component identifier for debugging and testing */
  component?: string;
  /** Column components to render within the grid */
  children?: ReactElement<typeof Column> | ReactElement<typeof Column>[],
}

/**
 * Grid component
 *
 * `Grid` is a responsive layout primitive based on a 12-column system. It wraps `Column` components and
 * applies consistent spacing and layout structure across screen sizes.
 *
 * Use it to create flexible, multi-column layouts that adapt responsively using Tailwind’s grid utilities.
 *
 *
 * @param tag - Optional HTML tag to render (div, ul, ol, article)
 * @param id - Optional HTML ID attribute for anchoring or in-page navigation
 * @param gap - Optional spacing between columns (`default`, `lg`, `md`, `sm`, etc.)
 * @param maxColumns - Optional limits number of columns shown at various breakpoints
 * @param theme - Optional theme applied to the grid for styling
 * @param className - Optional additional CSS classes for custom styling
 * @param component - Optional component identifier for debugging and testing
 * @param data-testid - Optional test ID used for automated testing
 * @param children - One or more `Column` components

 *
 * @example
 * ```tsx
 * <Grid gap="default">
 *   <Column spans={12} md={6}>
 *     <div>Left content</div>
 *   </Column>
 *   <Column spans={12} md={6}>
 *     <div>Right content</div>
 *   </Column>
 * </Grid>
 * ```
 *

 */

const Grid: FC<IGrid> = ({
  id,
  "data-testid": testId,
  tag = "div",
  gap = "default",
  maxColumns = "default",
  className = "",
  component = "Grid",
  theme,
  children,
}) => {
  const Tag = tag;
  return (
    <Tag
      data-testid={testId}
      data-component={component}
      className={cx(`grid ${gridMaxColumnsMap[maxColumns]} ${gridGapsMap[gap]} ${className}`)}
      id={id}
      data-theme={theme}
    >
      { children }
    </Tag>
  );
};

export default memo(Grid);
